import React, { useEffect, useState, lazy, Suspense } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  MapPin, Calendar, Building2, FileText, Download, Sparkles, Check,
  PlayCircle, ArrowLeft,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import GalleryGrid from '../components/profile/GalleryGrid';
import VideoEmbedCard from '../components/profile/VideoEmbedCard';
import { api, friendlyError } from '../lib/api';
import { track, EventTypes } from '../lib/analytics';

const PropertyMap = lazy(() => import('../components/PropertyMap'));

const STATUS_STYLE = {
  disponible: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  reservado: 'bg-amber-100 text-amber-700 border-amber-200',
  vendido: 'bg-rose-100 text-rose-700 border-rose-200',
  bloqueado: 'bg-gray-100 text-gray-600 border-gray-200',
};
const STATUS_LABEL = {
  disponible: 'Disponible',
  reservado: 'Reservado',
  vendido: 'Vendido',
  bloqueado: 'Bloqueado',
};

const PROJECT_STATUS_LABEL = {
  preventa: 'Preventa',
  en_construccion: 'En construcción',
  entrega: 'En entrega',
  entregado: 'Entregado',
  pausado: 'Pausado',
  cancelado: 'Cancelado',
};

function fmtCurrency(amount, currency) {
  if (amount == null) return '—';
  const cur = currency || 'COP';
  try {
    return new Intl.NumberFormat('es-CO', { style: 'currency', currency: cur, maximumFractionDigits: 0 }).format(amount);
  } catch {
    return `${cur} ${amount.toLocaleString('es-CO')}`;
  }
}

export default function ProjectProfile() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    setLoading(true);
    api.getProject(slug)
      .then(async (p) => {
        setProject(p);
        if (p?.id) {
          track(EventTypes.PROJECT_VIEWED, { project_id: p.id, developer_id: p.developer_id });
          const u = await api.listProjectUnits(p.id);
          setUnits(u?.items || []);
        }
      })
      .catch((e) => setErr(friendlyError(e, 'No pudimos cargar el proyecto.')))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-4 border-habita border-t-transparent rounded-full animate-spin" /></div>;
  if (err || !project) return (<><Header /><div className="max-w-xl mx-auto p-12 text-center" data-testid="project-not-found"><p>{err || 'Proyecto no encontrado'}</p></div><Footer /></>);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ApartmentComplex',
    name: project.name,
    description: project.description,
    image: project.gallery?.length ? project.gallery[0] : project.cover_url,
    url: typeof window !== 'undefined' ? window.location.href : '',
    address: {
      '@type': 'PostalAddress',
      addressLocality: project.city,
      addressCountry: project.country || 'CO',
    },
    geo: project.lat && project.lng ? { '@type': 'GeoCoordinates', latitude: project.lat, longitude: project.lng } : undefined,
    numberOfAccommodationUnits: project.units_total,
  };

  const availableUnits = units.filter(u => u.status === 'disponible').length;

  return (
    <div className="min-h-screen bg-habita-cream overflow-x-hidden">
      <SEO
        title={`${project.name} · ${project.developer_name || 'Proyecto Nuevo'}`}
        description={project.description?.slice(0, 160) || `${project.name}, proyecto inmobiliario en ${project.city}. ${availableUnits} unidades disponibles.`}
        image={project.gallery?.[0] || project.cover_url}
        jsonLd={jsonLd}
      />
      <Header />

      {/* HERO */}
      <div className="relative bg-habita-dark" data-testid="project-hero">
        <div className="h-72 md:h-96 overflow-hidden">
          {project.gallery?.[0]
            ? <img src={project.gallery[0]} alt={project.name} className="w-full h-full object-cover opacity-80" />
            : project.cover_url
              ? <img src={project.cover_url} alt={project.name} className="w-full h-full object-cover opacity-80" />
              : <div className="w-full h-full bg-gradient-to-br from-habita-dark to-habita" />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        </div>

        <div className="absolute inset-x-0 bottom-0 px-4 lg:px-8 pb-6">
          <div className="max-w-6xl mx-auto text-white">
            <div className="flex flex-wrap items-center gap-2 text-xs mb-2">
              {project.status && (
                <span className="px-2.5 py-1 rounded-full bg-white/95 text-habita-dark font-bold uppercase tracking-wider" data-testid="project-status-badge">
                  {PROJECT_STATUS_LABEL[project.status] || project.status}
                </span>
              )}
              {project.country && (
                <span className="px-2.5 py-1 rounded-full bg-white/20 text-white font-bold uppercase tracking-wider backdrop-blur" data-testid="project-country-badge">
                  {project.country}
                </span>
              )}
              {project.delivery_date && (
                <span className="px-2.5 py-1 rounded-full bg-white/20 text-white font-semibold backdrop-blur inline-flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Entrega {project.delivery_date}
                </span>
              )}
            </div>
            <h1 className="font-serif text-3xl lg:text-5xl font-bold drop-shadow-md" data-testid="project-name">{project.name}</h1>
            <p className="mt-1 text-sm md:text-base text-white/90 flex flex-wrap items-center gap-x-3 gap-y-1">
              {project.developer_slug ? (
                <Link to={`/constructora/${project.developer_slug}`} className="inline-flex items-center gap-1 hover:underline" data-testid="project-developer-link">
                  <Building2 className="w-4 h-4" /> {project.developer_name}
                </Link>
              ) : (
                <span className="inline-flex items-center gap-1"><Building2 className="w-4 h-4" /> {project.developer_name}</span>
              )}
              <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" /> {project.city}, {project.country}</span>
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-10 grid lg:grid-cols-3 gap-8">
        <main className="lg:col-span-2 space-y-6">
          {/* Price box */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-6 flex flex-wrap items-end gap-4" data-testid="project-price-box">
            <div className="w-full sm:flex-1 min-w-0">
              <p className="text-xs uppercase tracking-widest text-gray-500">Rango de precios</p>
              {project.price_min != null && project.price_max != null ? (
                <p className="font-serif font-bold text-habita mt-1" data-testid="project-price-range">
                  <span className="block sm:inline text-lg sm:text-2xl lg:text-3xl">desde {fmtCurrency(project.price_min, project.currency)}</span>
                  <span className="hidden sm:inline text-base text-gray-400 mx-1">·</span>
                  <span className="block sm:inline text-base sm:text-2xl lg:text-3xl text-habita-dark sm:text-habita">hasta {fmtCurrency(project.price_max, project.currency)}</span>
                </p>
              ) : (
                <p className="font-serif font-bold text-habita mt-1 text-lg sm:text-2xl" data-testid="project-price-range">
                  Consultar con la constructora
                </p>
              )}
            </div>
            {project.yield_estimate != null && (
              <div className="ml-auto bg-habita-mint rounded-xl px-4 py-2">
                <p className="text-[10px] uppercase tracking-widest text-habita-dark/70">Rentabilidad estimada</p>
                <p className="font-serif text-2xl font-bold text-habita">{project.yield_estimate}%</p>
              </div>
            )}
          </div>

          {/* Description */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h2 className="font-serif text-xl font-bold text-habita-dark">Sobre el proyecto</h2>
            <p className="text-sm text-gray-700 mt-3 whitespace-pre-line leading-relaxed" data-testid="project-description">{project.description || 'Sin descripción disponible.'}</p>
          </div>

          {/* Amenities */}
          {project.amenities?.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="project-amenities-card">
              <h2 className="font-serif text-xl font-bold text-habita-dark flex items-center gap-2"><Sparkles className="w-5 h-5 text-habita" /> Amenidades</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-4">
                {project.amenities.map(a => (
                  <div key={a} className="flex items-center gap-2 text-sm text-gray-700">
                    <span className="w-5 h-5 rounded-full bg-habita-mint flex items-center justify-center shrink-0"><Check className="w-3 h-3 text-habita" /></span>
                    {a}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Gallery */}
          {project.gallery?.length > 0 && (
            <GalleryGrid images={project.gallery} title="Galería" testid="project-gallery" altPrefix={`${project.name} galería`} />
          )}

          {/* Video */}
          {project.video_url && (
            <VideoEmbedCard url={project.video_url} title={`${project.name} · Video del proyecto`} testid="project-video-card" />
          )}

          {/* Floorplans */}
          {project.floorplans?.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="project-floorplans-card">
              <h2 className="font-serif text-xl font-bold text-habita-dark mb-4">Planos</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {project.floorplans.map((u, i) => (
                  <a key={u || `plan-${i}`} href={u} target="_blank" rel="noopener noreferrer" className="block rounded-xl overflow-hidden border border-gray-200 hover:border-habita transition-colors">
                    <img src={u} alt={`Plano ${i + 1}`} className="w-full aspect-[4/3] object-cover" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Map */}
          {project.lat != null && project.lng != null && (
            <div className="bg-white rounded-2xl border border-gray-200 p-6 overflow-hidden" data-testid="project-map-card">
              <h2 className="font-serif text-xl font-bold text-habita-dark mb-4 flex items-center gap-2"><MapPin className="w-5 h-5 text-habita" /> Ubicación</h2>
              <p className="text-sm text-gray-700 mb-3">{project.address || `${project.city}, ${project.country}`}</p>
              <div className="overflow-hidden rounded-2xl">
                <Suspense fallback={<div className="aspect-[16/9] rounded-xl bg-gray-100 animate-pulse" />}>
                  <PropertyMap lat={project.lat} lng={project.lng} label={project.name} />
                </Suspense>
              </div>
            </div>
          )}

          {/* Inventory table */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="project-inventory-card">
            <div className="flex items-end justify-between flex-wrap gap-2 mb-4">
              <div>
                <h2 className="font-serif text-xl font-bold text-habita-dark">Inventario</h2>
                <p className="text-xs text-gray-500 mt-1">
                  {project.units_total || units.length} unidades · {availableUnits} disponibles
                </p>
              </div>
              <div className="flex gap-1.5">
                {Object.entries(STATUS_LABEL).map(([k, label]) => (
                  <span key={k} className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${STATUS_STYLE[k]}`}>{label}</span>
                ))}
              </div>
            </div>
            {units.length === 0 ? (
              <p className="text-sm text-gray-500 py-6 text-center">Aún no hay unidades cargadas.</p>
            ) : (
              <div className="overflow-x-auto -mx-2">
                <table className="w-full text-sm" data-testid="project-inventory-table">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-gray-500 border-b border-gray-200">
                      <th className="py-2 px-2">Torre</th>
                      <th className="py-2 px-2">Unidad</th>
                      <th className="py-2 px-2">Piso</th>
                      <th className="py-2 px-2">Hab.</th>
                      <th className="py-2 px-2">Baños</th>
                      <th className="py-2 px-2">Área</th>
                      <th className="py-2 px-2 text-right">Precio</th>
                      <th className="py-2 px-2 text-center">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {units.map(u => (
                      <tr key={u.id} className="border-b border-gray-100 last:border-0 hover:bg-habita-cream/40 transition-colors" data-testid={`project-unit-${u.id}`}>
                        <td className="py-2 px-2 font-semibold">{u.tower || '—'}</td>
                        <td className="py-2 px-2">{u.unit_number}</td>
                        <td className="py-2 px-2 text-gray-600">{u.floor ?? '—'}</td>
                        <td className="py-2 px-2 text-gray-600">{u.beds ?? '—'}</td>
                        <td className="py-2 px-2 text-gray-600">{u.baths ?? '—'}</td>
                        <td className="py-2 px-2 text-gray-600">{u.area_m2 ? `${u.area_m2} m²` : '—'}</td>
                        <td className="py-2 px-2 text-right font-semibold text-habita-dark tabular-nums">{u.price != null ? fmtCurrency(u.price, u.currency) : '—'}</td>
                        <td className="py-2 px-2 text-center">
                          <span className={`inline-block text-[10px] px-2 py-0.5 rounded-full border font-bold uppercase tracking-wide ${STATUS_STYLE[u.status] || STATUS_STYLE.bloqueado}`} data-testid={`project-unit-status-${u.id}`}>
                            {STATUS_LABEL[u.status] || u.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>

        <aside className="space-y-4">
          {project.brochure_url && (
            <a
              href={project.brochure_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track('project.brochure_downloaded', { project_id: project.id })}
              className="block bg-habita rounded-2xl p-5 text-white hover:bg-habita-dark transition-colors"
              data-testid="project-brochure-btn"
            >
              <div className="flex items-center gap-3">
                <FileText className="w-10 h-10" />
                <div>
                  <p className="font-serif font-bold">Descargar brochure</p>
                  <p className="text-xs opacity-90 mt-0.5">PDF con toda la información</p>
                </div>
                <Download className="w-5 h-5 ml-auto" />
              </div>
            </a>
          )}

          {project.developer_slug && (
            <Link to={`/constructora/${project.developer_slug}`} className="block bg-white rounded-2xl border border-gray-200 p-5 hover:border-habita transition-colors" data-testid="project-developer-card">
              <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Constructora</p>
              <div className="flex items-center gap-3">
                {project.developer_logo
                  ? <img src={project.developer_logo} alt="" className="w-12 h-12 rounded-lg object-cover" />
                  : <div className="w-12 h-12 rounded-lg bg-habita-mint flex items-center justify-center text-habita"><Building2 className="w-6 h-6" /></div>}
                <div className="min-w-0">
                  <p className="font-semibold text-habita-dark truncate">{project.developer_name}</p>
                  <p className="text-xs text-habita font-semibold">Ver perfil →</p>
                </div>
              </div>
            </Link>
          )}

          <Link to="/proyectos" className="block text-sm text-gray-600 hover:text-habita font-semibold inline-flex items-center gap-1.5">
            <ArrowLeft className="w-4 h-4" /> Volver al listado
          </Link>

          {project.video_url && (
            <div className="bg-white rounded-2xl border border-gray-200 p-4 text-xs text-gray-500 flex items-center gap-2">
              <PlayCircle className="w-4 h-4 text-habita" /> Recorrido en video disponible
            </div>
          )}
        </aside>
      </div>

      <Footer />
    </div>
  );
}
